import { gql, NetworkStatus, useQuery } from '@apollo/client'
import { RouteComponentProps } from '@reach/router'
import cx from 'classnames'
import { format, parseISO } from 'date-fns'
import { Link } from 'gatsby'
import { FaSync } from 'react-icons/fa'

const sessionDetailsQuery = gql`
  query FullSessionDetails($sessionId: ID!) {
    rapidSession(id: $sessionId) {
      id
      startDate
      endDate
      reference
      thirdPartyId
      resultsUserEmails
      kits {
        id
        status
        result
      }
    }
  }
`

const RapidSession = ({ sessionId }: RouteComponentProps<{ sessionId: string }>) => {
  const { loading, error, data, refetch, networkStatus } = useQuery(sessionDetailsQuery, {
    variables: { sessionId },
    notifyOnNetworkStatusChange: true,
  })

  if (loading && !data) return <p>Loading...</p>

  if (error) throw error

  const session = data.rapidSession

  if (!session) {
    return <p>Not Found</p>
  }

  const positiveCount = session.kits.filter((k) => k.result === 'POSITIVE').length

  return (
    <>
      <section className="section">
        <div className="container">
          <h1 className="title">
            Session {session.thirdPartyId} &mdash; {session.reference}
          </h1>

          <p>
            <strong>Date: </strong> {format(parseISO(session.startDate), 'dd/MM/yyyy')}
          </p>

          <p>
            <strong>Users: </strong>
            {session.resultsUserEmails.map((email) => (
              <span key={email}>{email}</span>
            ))}
          </p>
        </div>
      </section>

      <section className="section mt-5">
        <nav className="level has-width-unset">
          <div className="level-item has-text-centered">
            <div>
              <p className="heading">Results</p>
              <p className="title">{session.kits.length}</p>
            </div>
          </div>

          <div className="level-item has-text-centered">
            <div>
              <p className="heading">Positive</p>
              <p
                className={cx('title', {
                  'has-text-danger': positiveCount > 0,
                })}
              >
                {positiveCount}
              </p>
            </div>
          </div>

          <div className="level-item has-text-centered">
            <div>
              <p className="heading">Negative</p>
              <p className="title">{session.kits.filter((k) => k.result === 'NEGATIVE').length}</p>
            </div>
          </div>

          <div className="level-item">
            <button
              className={cx('button is-info is-outlined', {
                'is-loading': networkStatus === NetworkStatus.refetch,
              })}
              onClick={() => refetch()}
            >
              <span className="icon is-small">
                <FaSync />
              </span>
            </button>
          </div>
        </nav>
        <div className="container">
          <table className="table is-bordered is-fullwidth">
            <thead>
              <tr>
                <th>Kit ID</th>
                <th>Status</th>
                <th>Result</th>
              </tr>
            </thead>

            <tbody>
              {session.kits.map((kit) => (
                <tr key={kit.id}>
                  <td>
                    <Link to={`/dashboard/kits?kitId=${kit.id}`}>{kit.id}</Link>
                  </td>
                  <td>{kit.status}</td>
                  <td
                    className={cx({
                      'has-text-danger': kit.result === 'POSITIVE',
                    })}
                  >
                    {kit.result}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  )
}

export default RapidSession
